// Wires the modules together and boots the viewer.
import { initAxisGizmo } from './axis-gizmo';
import { initControls } from './controls';
import { getDisplayMode, initDisplayMode, setDisplayMode, type DisplayMode } from './display';
import { canExplode, getExplodeAxis, getExplodeFactor, initExplode, resetExplode, setExplodeAxis, setExplodeFactor, type ExplodeAxis } from './explode';
import { cancelLoad, loadFile, modelLongestSideMm, onModelLoaded, refreshModelDims } from './import/loader';
import { initModelTree, setTreeOpen, toggleTreeOpen } from './model-tree';
import { canUseFilePicker, pickCadFile } from './open-file';
import { listRecentFiles, openRecentFile } from './recent-files';
import { animateToView, goHome, initViewCube } from './view-cube';
import * as THREE from 'three';
import {
  cancelPendingMeasure,
  clearMeasurement,
  isMeasureMode,
  setMeasureActivatedHook,
  setMeasureMode,
  setMeasurePanelOpen,
  toggleMeasureMode
} from './measure';
import { HATCH_FILL_IDS, LIGHTING_PRESETS, loadScreenshotOptions, saveScreenshotOptions, type HatchFillId, type LightingPreset, type ScreenshotScale } from './preferences';
import {
  flipSection,
  isSectionActive,
  isSectionPickMode,
  setOriginPlaneGuidesVisible,
  setSectionPickMode,
  startOriginSection,
  stopSection
} from './section';
import { getHatchFillId, setHatchFill } from './section-caps';
import { MODEL_UNITS, MODEL_UNIT_LABEL, getModelUnit, mmPerUnit, setCurrentModelUnit, suggestUnit, type ModelUnit } from './model-units';
import { checkForUpdate, resetAndReload } from './pwa';
import { initSettingsTabs } from './settings-tabs';
import { dom, getLengthUnit, initAlertDialog, onLengthUnitChange, isPanelOpen, log, logError, makePanelDraggable, setLengthUnit, setPanelOpen, showAlert, showConfirm } from './ui';
import {
  backgroundColor,
  fitCameraToModel,
  getBackgroundChoice,
  getLightingPreset,
  getModelGroup,
  initScene,
  isDark,
  setBackgroundChoice,
  isOrthographic,
  captureViewPng,
  screenshotFileName,
  requestRender,
  setLightingPreset,
  setTheme,
  toggleOrthographic
} from './viewer';
import {
  connectWamp,
  initWampInput,
  isWampConnected,
  toggleAxisDiag,
  wampTeardown
} from './wamp';

function wireLogPanel(): void {
  dom.logToggle.addEventListener('click', () => {
    const show = !dom.logPanel.classList.contains('show');
    dom.logPanel.classList.toggle('show', show);
    dom.logToggle.textContent = show ? 'Hide log' : 'Show log';
  });
}

/** Phones stack the tree and the tool panels in the same bottom sheet slot. */
function isPhoneLayout(): boolean {
  return window.matchMedia('(max-width: 720px)').matches;
}

/** One sheet at a time: opening a tool panel folds the tree away. */
function closeTreeSheet(): void {
  if (isPhoneLayout()) setTreeOpen(false);
}

function setSectionPanelOpen(open: boolean): void {
  if (open) closeTreeSheet();
  setPanelOpen(dom.sectionPanel, dom.sectionBtn, open);
  setOriginPlaneGuidesVisible(open);
}

function setExplodePanelOpen(open: boolean): void {
  setPanelOpen(dom.explodePanel, dom.explodeBtn, open);
  if (open) syncExplodePanel();
}

/**
 * The toolbar button carries the amount, so a model left in pieces with the
 * panel closed still says why it is in pieces.
 */
function syncExplodeButton(): void {
  const percent = Math.round(getExplodeFactor() * 100);
  // Same lit look as an active measure tool; there is no separate .active rule.
  dom.explodeBtn.classList.toggle('primary', percent > 0);
  dom.explodeBtn.title = percent > 0 ? `Explode view — ${percent}%` : 'Explode view';
}

/** Slider, percentage, axis buttons and the single-body note, all from state. */
function syncExplodePanel(): void {
  const possible = canExplode();
  dom.explodeSlider.disabled = !possible;
  dom.explodeEmpty.classList.toggle('hidden', possible);
  dom.explodeAxisRow.classList.toggle('hidden', !possible);

  const axis = getExplodeAxis();
  dom.explodeAxisRow.querySelectorAll<HTMLButtonElement>('.explode-axis').forEach(btn => {
    const on = btn.dataset.axis === axis;
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
    btn.classList.toggle('active', on);
  });
}

function wireExplodePanel(): void {
  // A new model has its own bodies, so the panel starts over with it.
  onModelLoaded(() => {
    dom.explodeSlider.value = '0';
    dom.explodeValue.textContent = '0%';
    syncExplodePanel();
    syncExplodeButton();
  });

  dom.explodeBtn.addEventListener('click', e => {
    e.stopPropagation();
    setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
    setSectionPanelOpen(false);
    if (isMeasureMode()) setMeasureMode(false);
    const open = !isPanelOpen(dom.explodePanel);
    if (open) closeTreeSheet();
    setExplodePanelOpen(open);
  });
  dom.explodeCloseBtn.addEventListener('click', () => setExplodePanelOpen(false));

  const sync = (): void => {
    const t = Number(dom.explodeSlider.value) / 100;
    setExplodeFactor(t);
    dom.explodeValue.textContent = `${Math.round(t * 100)}%`;
    syncExplodeButton();
  };
  dom.explodeSlider.addEventListener('input', sync);

  dom.explodeResetBtn.addEventListener('click', () => {
    resetExplode();
    dom.explodeSlider.value = '0';
    dom.explodeValue.textContent = '0%';
    syncExplodeButton();
  });

  dom.explodeAxisRow.addEventListener('click', e => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.explode-axis');
    const axis = btn?.dataset.axis as ExplodeAxis | undefined;
    if (!axis) return;
    setExplodeAxis(axis);
    syncExplodePanel();
  });
}

function wirePanels(): void {
  dom.settingsBtn.addEventListener('click', e => {
    e.stopPropagation();
    setSectionPanelOpen(false);
    if (isMeasureMode()) setMeasureMode(false);
    const open = !isPanelOpen(dom.settingsPanel);
    if (open) closeTreeSheet();
    setExplodePanelOpen(false);
    setPanelOpen(dom.settingsPanel, dom.settingsBtn, open);
  });

  dom.treeBtn.addEventListener('click', e => {
    e.stopPropagation();
    setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
    setSectionPanelOpen(false);
    setExplodePanelOpen(false);
    if (isMeasureMode()) setMeasureMode(false);
    toggleTreeOpen();
  });
  dom.sectionBtn.addEventListener('click', e => {
    e.stopPropagation();
    setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
    setExplodePanelOpen(false);
    if (isMeasureMode()) setMeasureMode(false);
    setSectionPanelOpen(!isPanelOpen(dom.sectionPanel));
  });

  document.addEventListener('click', e => {
    const target = e.target as Node;
    if (isPanelOpen(dom.settingsPanel) && !dom.settingsPanel.contains(target) && !dom.settingsBtn.contains(target)) {
      setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
    }
    if (isPanelOpen(dom.explodePanel) && !dom.explodePanel.contains(target) && !dom.explodeBtn.contains(target)) {
      setExplodePanelOpen(false);
    }
    // Keep measure / section panels open while those tools are active
    if (isMeasureMode() || isSectionActive()) return;
    if (isPanelOpen(dom.sectionPanel) && !dom.sectionPanel.contains(target) && !dom.sectionBtn.contains(target)) {
      setSectionPanelOpen(false);
    }
  });
}

/** Marks the chosen background, and parks the current colour in the picker. */
function syncBackgroundSwatches(): void {
  const active = getBackgroundChoice();
  let matched = false;
  dom.bgColorRow.querySelectorAll<HTMLButtonElement>('.bg-swatch').forEach(btn => {
    const on = btn.dataset.bg === active;
    if (on) matched = true;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  // A colour that is not one of the presets belongs to the picker.
  dom.bgCustom.parentElement?.classList.toggle('active', !matched);
  // Opening the picker should start from what is on screen, preset or not.
  dom.bgCustom.value = `#${backgroundColor().toString(16).padStart(6, '0')}`;
}

function setUpdateStatus(text: string, state: 'working' | 'done' | 'failed'): void {
  dom.updateStatus.textContent = text;
  dom.updateStatus.classList.remove('hidden', 'working', 'done', 'failed');
  dom.updateStatus.classList.add(state);
}

/**
 * The app keeps a copy of itself so it works offline, so a fresh deploy can
 * sit behind the cached one until something asks. These two buttons are that
 * something: one polite, one that throws the copy away.
 */
function wireUpdateButtons(): void {
  dom.checkUpdateBtn.addEventListener('click', () => {
    dom.checkUpdateBtn.disabled = true;
    setUpdateStatus('Checking…', 'working');
    void checkForUpdate().then(outcome => {
      if (outcome === 'updating') {
        // Taking over fires controllerchange, which reloads the page. If that
        // never arrives, say so rather than leaving a dead button behind.
        setUpdateStatus('New version found — reloading…', 'working');
        window.setTimeout(() => {
          dom.checkUpdateBtn.disabled = false;
          setUpdateStatus('The update did not take over. Try "Reload from scratch".', 'failed');
        }, 8000);
        return;
      }
      dom.checkUpdateBtn.disabled = false;
      if (outcome === 'current') {
        setUpdateStatus(`You are on the latest version (${__APP_VERSION__}).`, 'done');
      } else if (outcome === 'unsupported') {
        setUpdateStatus('No cached copy to update — this build is served straight from the network.', 'done');
      } else {
        setUpdateStatus('Could not reach the server to check. Try again when you are online.', 'failed');
      }
    });
  });

  dom.hardReloadBtn.addEventListener('click', () => {
    void showConfirm(
      'This drops the cached copy of the app and fetches everything again.',
      {
        title: 'Reload from scratch',
        detail: 'About 8 MB comes back over the network, so do it online. Your models, recent files and settings are not touched.',
        confirmLabel: 'Clear and reload',
        cancelLabel: 'Cancel'
      }
    ).then(go => {
      if (!go) return;
      setUpdateStatus('Clearing and reloading…', 'working');
      dom.checkUpdateBtn.disabled = true;
      dom.hardReloadBtn.disabled = true;
      void resetAndReload();
    });
  });
}

function syncHatchSwatches(): void {
  const active = getHatchFillId();
  dom.hatchColorRow.querySelectorAll<HTMLButtonElement>('.hatch-swatch').forEach(btn => {
    const id = btn.dataset.hatch as HatchFillId | undefined;
    btn.classList.toggle('active', id === active);
    btn.setAttribute('aria-checked', id === active ? 'true' : 'false');
  });
}

const DISPLAY_MODES: DisplayMode[] = ['shaded', 'edges', 'wireframe', 'xray', 'hidden'];

function syncLightingMenu(): void {
  const current = getLightingPreset();
  dom.displayMenu.querySelectorAll<HTMLButtonElement>('[data-light]').forEach(item => {
    const on = item.dataset.light === current;
    item.classList.toggle('active', on);
    item.setAttribute('aria-checked', on ? 'true' : 'false');
  });
}

function syncDisplayMenu(): void {
  const current = getDisplayMode();
  dom.displayMenu.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach(item => {
    const on = item.dataset.mode === current;
    item.classList.toggle('active', on);
    item.setAttribute('aria-checked', on ? 'true' : 'false');
    if (on) {
      // The toolbar button shows the active mode's icon.
      const icon = item.querySelector('svg');
      if (icon) dom.displayModeIcon.innerHTML = icon.outerHTML;
      const label = item.querySelector('span')?.textContent ?? '';
      dom.displayModeBtn.title = `Display mode: ${label}`;
    }
  });
}

/** Show or hide a toolbar dropdown, anchored under the button that opens it. */
function setToolMenuOpen(menu: HTMLElement, anchor: HTMLElement, opener: HTMLElement, open: boolean): void {
  if (open) {
    // parentElement, not offsetParent: a display:none menu has no offsetParent yet.
    const host = menu.parentElement;
    const hostRect = host?.getBoundingClientRect();
    const scale = host && hostRect ? hostRect.width / Math.max(host.offsetWidth, 1) : 1;
    menu.style.left = `${(anchor.getBoundingClientRect().left - (hostRect?.left ?? 0)) / scale}px`;
  }
  menu.classList.toggle('hidden', !open);
  opener.setAttribute('aria-expanded', open ? 'true' : 'false');
}

function setDisplayMenuOpen(open: boolean): void {
  if (open) setScreenshotMenuOpen(false);
  setToolMenuOpen(dom.displayMenu, dom.displayModeBtn, dom.displayModeBtn, open);
}

function setScreenshotMenuOpen(open: boolean): void {
  if (open) setDisplayMenuOpen(false);
  setToolMenuOpen(dom.screenshotMenu, dom.screenshotBtn, dom.screenshotMenuBtn, open);
}

let shotOptions = loadScreenshotOptions();

function syncScreenshotMenu(): void {
  const transparent = dom.screenshotMenu.querySelector<HTMLElement>('[data-shot="transparent"]');
  transparent?.classList.toggle('active', shotOptions.transparent);
  transparent?.setAttribute('aria-checked', shotOptions.transparent ? 'true' : 'false');
  dom.screenshotMenu.querySelectorAll<HTMLButtonElement>('[data-scale]').forEach(btn => {
    const on = Number(btn.dataset.scale) === shotOptions.scale;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
  });
  const extras = [shotOptions.scale + '×', shotOptions.transparent ? 'transparent' : ''].filter(Boolean).join(', ');
  dom.screenshotBtn.title = `Save view (PNG, ${extras})`;
}

/** True when this browser can hand a PNG to the OS share sheet. */
function canShareView(): boolean {
  const probe = new File([new Uint8Array(1)], 'view.png', { type: 'image/png' });
  return typeof navigator.canShare === 'function' && navigator.canShare({ files: [probe] });
}

/**
 * Share the rendered view through the OS. On a phone this is the useful one:
 * downloading a PNG in Safari just opens it, while the share sheet reaches
 * Mail, WhatsApp and AirDrop.
 */
async function shareView(): Promise<void> {
  try {
    const blob = await captureViewPng(shotOptions);
    const name = screenshotFileName(dom.modelTitle.textContent || 'view');
    const file = new File([blob], name, { type: 'image/png' });
    if (!navigator.canShare?.({ files: [file] })) {
      await takeScreenshot('save');
      return;
    }
    await navigator.share({ files: [file], title: dom.modelTitle.textContent || '3D Viewer' });
    log('View shared.', true);
  } catch (err) {
    // Dismissing the share sheet is an AbortError, not a failure worth reporting.
    if (err instanceof DOMException && err.name === 'AbortError') return;
    logError('Sharing failed: ' + (err instanceof Error ? err.message : String(err)));
  }
}

async function takeScreenshot(target: 'save' | 'copy'): Promise<void> {
  try {
    const blob = await captureViewPng(shotOptions);
    if (target === 'copy') {
      const Item = (window as Window & { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
      if (!Item || !navigator.clipboard?.write) throw new Error('this browser cannot copy images');
      await navigator.clipboard.write([new Item({ 'image/png': blob })]);
      log('View copied to the clipboard.', true);
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = screenshotFileName(dom.modelTitle.textContent || 'view');
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    log('View saved as PNG.', true);
  } catch (err) {
    logError('Screenshot failed: ' + (err instanceof Error ? err.message : String(err)));
  }
}

function wireScreenshotMenu(): void {
  syncScreenshotMenu();
  dom.shareViewBtn.classList.toggle('hidden', !canShareView());
  dom.screenshotMenuBtn.addEventListener('click', e => {
    e.stopPropagation();
    setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
    setScreenshotMenuOpen(dom.screenshotMenu.classList.contains('hidden'));
  });
  dom.screenshotMenu.addEventListener('click', e => {
    e.stopPropagation();
    const el = e.target as HTMLElement;
    const scaleBtn = el.closest<HTMLButtonElement>('[data-scale]');
    if (scaleBtn) {
      shotOptions = { ...shotOptions, scale: Number(scaleBtn.dataset.scale) as ScreenshotScale };
      saveScreenshotOptions(shotOptions);
      syncScreenshotMenu();
      return;
    }
    const item = el.closest<HTMLButtonElement>('[data-shot]');
    const action = item?.dataset.shot;
    if (action === 'transparent') {
      shotOptions = { ...shotOptions, transparent: !shotOptions.transparent };
      saveScreenshotOptions(shotOptions);
      syncScreenshotMenu();
    } else if (action === 'share') {
      setScreenshotMenuOpen(false);
      void shareView();
    } else if (action === 'save' || action === 'copy') {
      setScreenshotMenuOpen(false);
      void takeScreenshot(action);
    }
  });
  document.addEventListener('click', e => {
    if (!dom.screenshotMenu.contains(e.target as Node)) setScreenshotMenuOpen(false);
  });
}

function wireDisplayMenu(): void {
  syncDisplayMenu();
  syncLightingMenu();
  dom.displayModeBtn.addEventListener('click', e => {
    e.stopPropagation();
    setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
    setDisplayMenuOpen(dom.displayMenu.classList.contains('hidden'));
  });
  dom.displayMenu.addEventListener('click', e => {
    e.stopPropagation();
    const item = (e.target as HTMLElement).closest<HTMLButtonElement>('.tool-menu-item');
    if (!item) return;
    const light = item.dataset.light as LightingPreset | undefined;
    if (light && LIGHTING_PRESETS.includes(light)) {
      setLightingPreset(light);
      syncLightingMenu();
      setDisplayMenuOpen(false);
      return;
    }
    const mode = item.dataset.mode as DisplayMode | undefined;
    if (!mode || !DISPLAY_MODES.includes(mode)) return;
    setDisplayMode(mode);
    syncDisplayMenu();
    setDisplayMenuOpen(false);
  });
  document.addEventListener('click', e => {
    if (!dom.displayMenu.contains(e.target as Node)) setDisplayMenuOpen(false);
  });
}

function syncOrthoToggle(): void {
  const on = isOrthographic();
  dom.orthoToggle.setAttribute('aria-pressed', on ? 'true' : 'false');
  dom.orthoToggle.classList.toggle('active', on);
  dom.orthoToggle.textContent = on ? 'Perspective' : 'Orthographic';
  dom.orthoToggle.title = on ? 'Switch to perspective' : 'Switch to orthographic';
}

function syncUnitToggle(): void {
  const unit = getLengthUnit();
  dom.unitToggle.querySelectorAll<HTMLButtonElement>('.unit-opt').forEach(btn => {
    const on = btn.dataset.unit === unit;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
  });
}

function wireDisplaySettings(): void {
  syncUnitToggle();
  dom.unitToggle.addEventListener('click', e => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.unit-opt');
    const unit = btn?.dataset.unit;
    if (unit !== 'mm' && unit !== 'in') return;
    setLengthUnit(unit);
    syncUnitToggle();
  });
  dom.themeToggle.addEventListener('click', () => setTheme(!isDark()));
  dom.axisDiagToggle.addEventListener('click', toggleAxisDiag);
  syncOrthoToggle();
  dom.orthoToggle.addEventListener('click', () => {
    toggleOrthographic();
    syncOrthoToggle();
  });
  wireUpdateButtons();

  syncBackgroundSwatches();
  dom.bgColorRow.addEventListener('click', e => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.bg-swatch');
    const value = btn?.dataset.bg;
    if (!value) return;
    setBackgroundChoice(value);
    syncBackgroundSwatches();
  });
  // 'input' rather than 'change': the view follows the picker while it is open.
  dom.bgCustom.addEventListener('input', () => {
    setBackgroundChoice(dom.bgCustom.value);
    syncBackgroundSwatches();
  });

  syncHatchSwatches();
  dom.hatchColorRow.addEventListener('click', e => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.hatch-swatch');
    const id = btn?.dataset.hatch as HatchFillId | undefined;
    if (!id || !HATCH_FILL_IDS.includes(id)) return;
    setHatchFill(id);
    syncHatchSwatches();
  });
}

function formatRecentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function openCadFile(): Promise<void> {
  if (canUseFilePicker()) {
    try {
      const picked = await pickCadFile();
      if (!picked) return;
      const ok = await loadFile(picked.file, { handle: picked.handle });
      if (ok) void refreshRecentFiles();
      return;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logError('Could not open file picker: ' + message);
      // Fall through to the legacy input as a safety net.
    }
  }
  dom.fileInput.click();
}

async function reopenRecent(id: string): Promise<void> {
  const opened = await openRecentFile(id);
  if (!opened) {
    // Moved, deleted, or permission withdrawn. Opening the picker from here
    // would be silently ignored — the user gesture is spent by the await.
    showAlert('That file is no longer available where it was opened from. Choose it again with Open file.', {
      title: 'Cannot reopen this file'
    });
    void refreshRecentFiles();
    return;
  }
  const ok = await loadFile(opened.file, { handle: opened.handle });
  if (ok) void refreshRecentFiles();
}

async function refreshRecentFiles(): Promise<void> {
  const recent = await listRecentFiles();
  dom.recentFilesList.replaceChildren();
  if (recent.length === 0) {
    dom.recentFiles.classList.add('hidden');
    return;
  }

  for (const entry of recent) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'recent-file-btn';
    btn.title = `Open ${entry.name}`;

    const name = document.createElement('span');
    name.className = 'recent-file-name';
    name.textContent = entry.name;

    const meta = document.createElement('span');
    meta.className = 'recent-file-meta';
    meta.textContent = formatRecentSize(entry.size);

    btn.append(name, meta);
    btn.addEventListener('click', () => {
      void reopenRecent(entry.id);
    });
    li.appendChild(btn);
    dom.recentFilesList.appendChild(li);
  }

  dom.recentFiles.classList.remove('hidden');
}

function wireFileInput(): void {
  dom.fileBtn.addEventListener('click', () => {
    void openCadFile();
  });
  dom.loadBtn.addEventListener('click', () => {
    void openCadFile();
  });
  dom.fileInput.addEventListener('change', () => {
    const file = dom.fileInput.files?.[0];
    if (file) {
      void loadFile(file).then(ok => {
        if (ok) void refreshRecentFiles();
      });
    }
    dom.fileInput.value = '';
  });

  (['dragenter', 'dragover'] as const).forEach(evt => {
    window.addEventListener(evt, e => {
      e.preventDefault();
      dom.dropZone.classList.remove('hidden');
      dom.dropZone.classList.add('dragover');
      dom.dropCard.classList.add('dragover');
    });
  });
  (['dragleave', 'drop'] as const).forEach(evt => {
    window.addEventListener(evt, e => {
      e.preventDefault();
      dom.dropZone.classList.remove('dragover');
      dom.dropCard.classList.remove('dragover');
    });
  });
  window.addEventListener('drop', e => {
    e.preventDefault();
    dom.dropZone.classList.remove('dragover');
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      void loadFile(file).then(ok => {
        if (ok) void refreshRecentFiles();
      });
    } else if (getModelGroup()) {
      dom.dropZone.classList.add('hidden');
    }
  });
}

interface FileLaunchParams {
  files: FileSystemFileHandle[];
}

/** Receive files opened through the installed PWA's operating-system association. */
function wireFileHandler(): void {
  const launchQueue = (window as Window & {
    launchQueue?: {
      setConsumer(consumer: (params: FileLaunchParams) => void): void;
    };
  }).launchQueue;

  if (!launchQueue) return;

  launchQueue.setConsumer(params => {
    const handle = params.files[0];
    if (!handle) return;

    void handle.getFile()
      .then(async file => {
        const ok = await loadFile(file, { handle });
        if (ok) void refreshRecentFiles();
      })
      .catch(err => {
        const message = err instanceof Error ? err.message : String(err);
        logError('Could not open the selected file: ' + message);
      });
  });
}

function wireViewButtons(): void {
  dom.screenshotBtn.addEventListener('click', () => void takeScreenshot('save'));

  dom.measureBtn.addEventListener('click', e => {
    e.stopPropagation();
    setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
    setSectionPanelOpen(false);
    toggleMeasureMode();
  });
  dom.clearMeasureBtn.addEventListener('click', clearMeasurement);
}

function wireSectionButtons(): void {
  dom.sectionPickBtn.addEventListener('click', () => setSectionPickMode(!isSectionPickMode()));
  dom.sectionXYBtn.addEventListener('click', () => startOriginSection('xy'));
  dom.sectionXZBtn.addEventListener('click', () => startOriginSection('xz'));
  dom.sectionYZBtn.addEventListener('click', () => startOriginSection('yz'));
  dom.sectionFlipBtn.addEventListener('click', flipSection);
  dom.sectionOffBtn.addEventListener('click', stopSection);
  dom.sectionCloseBtn.addEventListener('click', () => setSectionPanelOpen(false));
  dom.measureCloseBtn.addEventListener('click', () => setMeasureMode(false));
  dom.settingsCloseBtn.addEventListener('click', () => setPanelOpen(dom.settingsPanel, dom.settingsBtn, false));
}

/**
 * The file-units chip. STEP and IGES carry their unit, but a mesh file may not,
 * and reading it wrong makes every measurement off by a constant factor — so
 * the assumption is on screen next to the size, and changeable.
 */
function syncModelUnitUi(): void {
  const unit = getModelUnit();
  dom.modelUnitBtn.textContent = unit;
  dom.modelUnitBtn.title = `One unit in the file is read as 1 ${MODEL_UNIT_LABEL[unit].toLowerCase().replace(/s$/, '')}`;
  dom.modelUnitMenu.querySelectorAll<HTMLButtonElement>('[data-unit]').forEach(item => {
    const on = item.dataset.unit === unit;
    item.classList.toggle('active', on);
    item.setAttribute('aria-checked', on ? 'true' : 'false');
  });

  const suggestion = suggestUnit(modelLongestSideMm(), unit);
  dom.modelUnitHint.classList.toggle('hidden', !suggestion);
  if (suggestion) {
    dom.modelUnitHint.textContent = `${suggestion}?`;
    dom.modelUnitHint.title = `This model is an unusual size. Read it as ${MODEL_UNIT_LABEL[suggestion].toLowerCase()} instead?`;
    dom.modelUnitHint.dataset.unit = suggestion;
  }
}

function setModelUnitMenuOpen(open: boolean): void {
  dom.modelUnitMenu.classList.toggle('hidden', !open);
  dom.modelUnitBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
}

/** Re-read the model at a different unit: rescale, refit, and start clean. */
function applyModelUnit(unit: ModelUnit): void {
  const group = getModelGroup();
  if (!group || unit === getModelUnit()) return;

  setCurrentModelUnit(unit);
  // Existing measurements and the section plane were taken at the old scale.
  clearMeasurement();
  stopSection();
  group.scale.setScalar(mmPerUnit(unit));
  group.updateMatrixWorld(true);
  fitCameraToModel();
  initExplode();
  dom.explodeSlider.value = '0';
  dom.explodeValue.textContent = '0%';
  syncExplodePanel();
  syncExplodeButton();
  refreshModelDims();
  syncModelUnitUi();
  requestRender();
}

function wireModelUnits(): void {
  for (const unit of MODEL_UNITS) {
    const item = document.createElement('button');
    item.type = 'button';
    item.className = 'tool-menu-item';
    item.setAttribute('role', 'menuitemradio');
    item.dataset.unit = unit;
    const name = document.createElement('span');
    name.textContent = `${MODEL_UNIT_LABEL[unit]} (${unit})`;
    item.appendChild(name);
    dom.modelUnitMenu.appendChild(item);
  }

  dom.modelUnitBtn.addEventListener('click', e => {
    e.stopPropagation();
    setModelUnitMenuOpen(dom.modelUnitMenu.classList.contains('hidden'));
  });
  dom.modelUnitMenu.addEventListener('click', e => {
    e.stopPropagation();
    const unit = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-unit]')?.dataset.unit;
    if (!unit) return;
    setModelUnitMenuOpen(false);
    applyModelUnit(unit as ModelUnit);
  });
  dom.modelUnitHint.addEventListener('click', e => {
    e.stopPropagation();
    const unit = dom.modelUnitHint.dataset.unit;
    if (unit) applyModelUnit(unit as ModelUnit);
  });
  document.addEventListener('click', () => setModelUnitMenuOpen(false));

  onModelLoaded(syncModelUnitUi);
  onLengthUnitChange(syncModelUnitUi);
  syncModelUnitUi();
}

function wireDraggablePanels(): void {
  makePanelDraggable(dom.measurePanel, dom.measurePanelDrag);
  makePanelDraggable(dom.sectionPanel, dom.sectionPanelDrag);
  makePanelDraggable(dom.explodePanel, dom.explodePanelDrag);
  // Keep clicks inside floating panels from collapsing other chrome.
  for (const panel of [dom.measurePanel, dom.sectionPanel, dom.explodePanel]) {
    panel.addEventListener('click', e => e.stopPropagation());
  }
}

function wireKeyboard(): void {
  window.addEventListener('keydown', e => {
    const target = e.target as HTMLElement | null;
    const typing =
      target &&
      (target.tagName === 'TEXTAREA' ||
        (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'text'));
    if (typing) return;

    if (e.key === 'Escape') {
      if (isMeasureMode()) { cancelPendingMeasure(); setMeasureMode(false); }
      if (isSectionPickMode()) setSectionPickMode(false);
      setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
      setSectionPanelOpen(false);
      setMeasurePanelOpen(false);
      setDisplayMenuOpen(false);
      setScreenshotMenuOpen(false);
      return;
    }

    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    // Measure / section need a model (their toolbar buttons are hidden until then).
    if (!getModelGroup()) return;

    // CAD-style standard views (numpad layout): 1 front, 3 right, 7 top, 0 iso, F fit.
    const views: Record<string, [number, number, number]> = {
      '1': [0, 0, 1], '3': [1, 0, 0], '7': [0, 1, 0], '0': [1, 0.85, 1]
    };
    if (!e.ctrlKey && !e.metaKey && !e.altKey && views[e.key]) {
      animateToView(new THREE.Vector3(...views[e.key]).normalize());
      return;
    }
    if (e.key === 'f' || e.key === 'F') {
      goHome();
      return;
    }

    if (e.key === 'm' || e.key === 'M') {
      setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
      setSectionPanelOpen(false);
      toggleMeasureMode();
    } else if (e.key === 's' || e.key === 'S') {
      setPanelOpen(dom.settingsPanel, dom.settingsBtn, false);
      if (isMeasureMode()) setMeasureMode(false);
      setSectionPanelOpen(!isPanelOpen(dom.sectionPanel));
    }
  });
}

function wireWampButtons(): void {
  dom.connectWampBtn.addEventListener('click', () => void connectWamp());
  dom.disconnectBtn.addEventListener('click', () => {
    if (isWampConnected()) wampTeardown(false);
  });
}

export function boot(): void {
  // Build stamp in Settings: the quickest way to tell whether a device —
  // an installed PWA especially — is running the latest deploy.
  dom.appVersion.textContent = __APP_VERSION__;
  initAlertDialog();
  initScene();
  initDisplayMode();
  initAxisGizmo();
  initViewCube();
  initModelTree();
  initControls();
  initWampInput();
  wireLogPanel();
  initSettingsTabs();
  wirePanels();
  wireDraggablePanels();
  wireDisplaySettings();
  wireDisplayMenu();
  wireScreenshotMenu();
  syncOrthoToggle();
  wireFileInput();
  wireFileHandler();
  void refreshRecentFiles();
  wireViewButtons();
  wireSectionButtons();
  wireModelUnits();
  wireExplodePanel();
  wireKeyboard();
  wireWampButtons();
  dom.cancelLoadBtn.addEventListener('click', () => cancelLoad());

  setMeasureActivatedHook(() => {
    if (isSectionPickMode()) setSectionPickMode(false);
    setSectionPanelOpen(false);
    setExplodePanelOpen(false);
    closeTreeSheet();
  });

  log('Viewer ready — drop a CAD or mesh file on the window to begin.', true);

  // Same pattern as 3Dconnexion's own THREE.js sample (body onload ->
  // init3DMouse() -> connect()): try the driver immediately on page load, no
  // button press required. Quiet on failure -- this is a background attempt,
  // not a user action, so no popup, just the log panel and the status line.
  // The Connect button still works for a manual retry.
  void connectWamp();
}
